import { Collab, Campaign, BountyClaim } from "@foodfluence/shared";
import { isRetailCampaignType, isShippingCampaignType } from "./collabUtils";

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

export interface MonthlyEarnings {
  month: number;
  monthLabel: string;
  cash: number;
  credit: number;
  total: number;
}

export type TimeRange = '1W' | '1M' | '3M' | 'YTD' | '1Y' | 'ALL';

function toDate(dateLike: { toDate?: () => Date; seconds?: number } | null | undefined): Date | null {
  if (!dateLike) return null;
  if (typeof dateLike.toDate === 'function') return dateLike.toDate();
  if (typeof dateLike.seconds === 'number') return new Date(dateLike.seconds * 1000);
  return null;
}

/** Returns a YYYY-MM-DD string for a given Date. */
const dateKey = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Returns the cash, credit, or combined amount for a single collab. */
function getCollabAmount(
  collab: Collab,
  campaign: Campaign | undefined,
  type: 'cash' | 'credit' | 'both'
): number {
  const cash = collab.cash ?? 0;
  let credit = collab.credit ?? 0;
  const productValue = campaign?.productValue ?? 0;
  if (productValue > 0 && (isRetailCampaignType(campaign?.campaignType) || isShippingCampaignType(campaign?.campaignType))) {
    credit += productValue;
  }
  return type === 'cash' ? cash : type === 'credit' ? credit : cash + credit;
}

/**
 * Resolves the best available date for placing a completed collab's earnings on the chart.
 * Priority: collabCompleted log → collabRedeemedTime → last pendingVerification log.
 */
function getEarnedDate(collab: Collab): Date | null {
  return (
    toDate(collab.logs?.find(l => l.status === 'collabCompleted')?.timestamp) ??
    toDate(collab.collabRedeemedTime) ??
    toDate([...( collab.logs ?? [])].reverse().find(l => l.status === 'pendingVerification')?.timestamp)
  );
}

/** Builds a Map<YYYY-MM-DD, amount> for completed collabs. */
function buildDailyTotalsMap(
  collabs: Collab[],
  campaignData: Record<string, Campaign>,
  type: 'cash' | 'credit' | 'both'
): Map<string, number> {
  const map = new Map<string, number>();
  collabs.filter(c => c.status === 'collabCompleted').forEach(collab => {
    const earnedDate = getEarnedDate(collab);
    if (!earnedDate) return;
    const key = dateKey(earnedDate);
    map.set(key, (map.get(key) ?? 0) + getCollabAmount(collab, campaignData[collab.id], type));
  });
  return map;
}

/**
 * Returns monthly earnings (cash and credit) for completed collabs in the given year.
 * Uses the collabCompleted log timestamp for the earned date.
 * For retail, shopify, and shipToHome campaigns, productValue is added to credit.
 */
export function getMonthlyEarningsByType(
  collabs: Collab[],
  campaignData: Record<string, Campaign>,
  year: number = new Date().getFullYear()
): MonthlyEarnings[] {
  const monthlyTotals: MonthlyEarnings[] = Array.from({ length: 12 }, (_, i) => ({
    month: i + 1,
    monthLabel: MONTH_LABELS[i],
    cash: 0,
    credit: 0,
    total: 0,
  }));

  collabs.filter(c => c.status === 'collabCompleted').forEach(collab => {
    const earnedDate = getEarnedDate(collab);
    if (!earnedDate || earnedDate.getFullYear() !== year) return;

    const monthIndex = earnedDate.getMonth();
    const campaign = campaignData[collab.id];
    const cash = getCollabAmount(collab, campaign, 'cash');
    const credit = getCollabAmount(collab, campaign, 'credit');
    monthlyTotals[monthIndex].cash += cash;
    monthlyTotals[monthIndex].credit += credit;
    monthlyTotals[monthIndex].total += cash + credit;
  });

  return monthlyTotals;
}

/**
 * Transforms monthly earnings into chart-ready format (month + value for selected type).
 */
export function getMonthlyEarningsChartData(
  monthlyEarnings: MonthlyEarnings[],
  type: 'cash' | 'credit' | 'both'
): { month: string; value: number }[] {
  return monthlyEarnings.map(m => ({
    month: m.monthLabel,
    value: type === 'cash' ? m.cash : type === 'credit' ? m.credit : m.cash + m.credit,
  }));
}

/**
 * Returns total earnings for a given type (cash, credit, or both) in the current year.
 */
export function getTotalEarningsForType(
  monthlyEarnings: MonthlyEarnings[],
  type: 'cash' | 'credit' | 'both'
): number {
  return monthlyEarnings.reduce(
    (sum, m) => sum + (type === 'cash' ? m.cash : type === 'credit' ? m.credit : m.cash + m.credit),
    0
  );
}

/**
 * Builds an all-time monthly earnings array (one entry per calendar month that has data),
 * ordered chronologically.
 */
function buildAllTimeMonthlyEarnings(
  collabs: Collab[],
  campaignData: Record<string, Campaign>
): (MonthlyEarnings & { year: number })[] {
  const years = [...new Set(
    collabs
      .filter(c => c.status === 'collabCompleted')
      .map(c => getEarnedDate(c)?.getFullYear())
      .filter((y): y is number => y !== undefined)
  )].sort();

  return years.flatMap(year =>
    getMonthlyEarningsByType(collabs, campaignData, year)
      .filter(m => m.cash > 0 || m.credit > 0)
      .map(m => ({ ...m, year }))
  );
}

/**
 * Builds daily earnings for the last N days. Returns cumulative earnings per day.
 */
function buildDailyEarnings(
  collabs: Collab[],
  campaignData: Record<string, Campaign>,
  daysCount: number,
  type: 'cash' | 'credit' | 'both'
): { month: string; value: number }[] {
  const dailyTotals = buildDailyTotalsMap(collabs, campaignData, type);

  const result: { month: string; value: number }[] = [];
  let cumulative = 0;
  for (let i = daysCount - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    d.setHours(0, 0, 0, 0);
    cumulative += dailyTotals.get(dateKey(d)) ?? 0;
    const label = i === 0 ? 'Today' : i === 1 ? 'Yesterday' : `${MONTH_LABELS[d.getMonth()]} ${d.getDate()}`;
    result.push({ month: label, value: cumulative });
  }
  return result;
}

/**
 * Builds 4 weekly earnings points for the last 30 days. Returns cumulative per week.
 */
function buildWeeklyEarnings(
  collabs: Collab[],
  campaignData: Record<string, Campaign>,
  type: 'cash' | 'credit' | 'both'
): { month: string; value: number }[] {
  const dailyTotals = buildDailyTotalsMap(collabs, campaignData, type);

  const result: { month: string; value: number }[] = [];
  let cumulative = 0;
  for (let w = 3; w >= 0; w--) {
    // Week w: days (w*7) to (w*7+6) ago, inclusive. w=3 is oldest (days 22-28 ago), w=0 is newest (days 0-6 ago, includes today)
    const startDay = w * 7 + 7;
    const endDay = w === 0 ? 0 : w * 7 + 1; // w=0 includes today (day 0)
    for (let d = startDay; d >= endDay; d--) {
      const date = new Date();
      date.setDate(date.getDate() - d);
      date.setHours(0, 0, 0, 0);
      cumulative += dailyTotals.get(dateKey(date)) ?? 0;
    }
    const label = w === 0 ? 'This week' : w === 1 ? '2w ago' : w === 2 ? '3w ago' : '4w ago';
    result.push({ month: label, value: cumulative });
  }
  return result;
}

/**
 * Returns chart data filtered by the given time range.
 * For 1W: daily (7 points). For 1M: weekly (4 points). For ALL: monthly.
 */
export function getFilteredChartData(
  collabs: Collab[],
  campaignData: Record<string, Campaign>,
  timeRange: TimeRange,
  type: 'cash' | 'credit' | 'both'
): { month: string; value: number }[] {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0-based

  const getValue = (m: MonthlyEarnings): number =>
    type === 'cash' ? m.cash : type === 'credit' ? m.credit : m.cash + m.credit;

  if (timeRange === 'ALL') {
    const all = buildAllTimeMonthlyEarnings(collabs, campaignData);
    const data = all.map(m => ({ month: `${m.monthLabel} ${m.year}`, value: getValue(m) }));
    if (data.length === 1) {
      return [{ month: '', value: 0 }, ...data];
    }
    return data;
  }

  if (timeRange === '1W') {
    return buildDailyEarnings(collabs, campaignData, 7, type);
  }
  if (timeRange === '1M') {
    return buildWeeklyEarnings(collabs, campaignData, type);
  }

  // For 3M, YTD, 1Y: monthly slots
  let monthsBack: number;
  if (timeRange === '3M') {
    monthsBack = 3;
  } else if (timeRange === 'YTD') {
    monthsBack = currentMonth + 1;
  } else {
    monthsBack = 12;
  }

  const slots: { year: number; monthIndex: number }[] = [];
  for (let i = monthsBack - 1; i >= 0; i--) {
    let m = currentMonth - i;
    let y = currentYear;
    if (m < 0) { m += 12; y -= 1; }
    slots.push({ year: y, monthIndex: m });
  }

  const yearsNeeded = [...new Set(slots.map(s => s.year))];
  const earningsByYearMonth = new Map<string, number>();
  yearsNeeded.forEach(year => {
    const monthly = getMonthlyEarningsByType(collabs, campaignData, year);
    monthly.forEach(m => {
      earningsByYearMonth.set(`${year}-${m.month - 1}`, getValue(m));
    });
  });

  return slots.map(({ year, monthIndex }) => ({
    month: monthsBack > 6 ? `${MONTH_LABELS[monthIndex]} ${year}` : MONTH_LABELS[monthIndex],
    value: earningsByYearMonth.get(`${year}-${monthIndex}`) ?? 0,
  }));
}

/**
 * Returns total all-time earnings for all completed collabs, without requiring log entries.
 * Used for the "Total Earned" display stat — not time-range filtered.
 */
export function getAllTimeEarnings(
  collabs: Collab[],
  campaignData: Record<string, Campaign>,
  type: 'cash' | 'credit' | 'both'
): number {
  return collabs
    .filter(c => c.status === 'collabCompleted')
    .reduce((sum, collab) => sum + getCollabAmount(collab, campaignData[collab.id], type), 0);
}

/**
 * Returns the total earnings for a given time range and type.
 */
export function getTotalEarningsForTimeRange(
  collabs: Collab[],
  campaignData: Record<string, Campaign>,
  timeRange: TimeRange,
  type: 'cash' | 'credit' | 'both'
): number {
  const data = getFilteredChartData(collabs, campaignData, timeRange, type);
  // 1W/1M use cumulative values; total is the last point
  if (timeRange === '1W' || timeRange === '1M') {
    return data.length > 0 ? data[data.length - 1].value : 0;
  }
  return data.reduce((sum, d) => sum + d.value, 0);
}

/** All-time bounty earnings: every verified claim, whether or not it has been withdrawn yet. */
export function getTotalBountyEarnings(claims: BountyClaim[]): number {
  return claims
    .filter(c => c.status === 'verified')
    .reduce((sum, c) => sum + (c.payoutAmount ?? 0), 0);
}

/**
 * Earnings for tight display slots: whole dollars with separators up to $9,999,
 * then compact ($28.9K, $1.2M) so big totals don't overflow their tile.
 */
export function formatEarningsShort(amount: number): string {
  const value = Math.round(amount);
  if (Math.abs(value) < 10000) return `$${value.toLocaleString('en-US')}`;
  return `$${new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(value)}`;
}

/**
 * Earnings for the narrowest tiles (profile stats strip, ~4 characters at 32px):
 * whole dollars under $1,000, then compact with no decimals ($29K) until millions ($1.3M).
 */
export function formatEarningsTile(amount: number): string {
  const value = Math.round(amount);
  if (Math.abs(value) < 1000) return `$${value}`;
  const maximumFractionDigits = Math.abs(value) >= 1_000_000 ? 1 : 0;
  return `$${new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits }).format(value)}`;
}
